<template>
  <div class="cs-columns">
    <!-- Graph State -->
    <div v-if="!graph" class="cs-columns__empty">
      <v-progress-circular
        v-if="graphLoading"
        indeterminate
        size="28"
        width="3"
        color="primary" />
      <v-icon v-else size="28" color="error">mdi-database-alert-outline</v-icon>
      <span class="cs-columns__muted">
        {{ graphLoading
          ? 'Loading the columns available from this starting data…'
          : 'The columns for this starting data could not be loaded.' }}
      </span>
      <v-btn
        v-if="!graphLoading"
        text
        small
        color="error"
        @click="$emit('retry-graph')">
        Try again
      </v-btn>
    </div>

    <template v-else>
      <div class="cs-columns-hz">
        <!-- ====================================================================
             TOP PANEL: SOURCE CATALOG & COLUMN PICKER (FULL WIDTH)
             ==================================================================== -->
        <div class="cs-hz-panel cs-catalog-section">
          <!-- Industrial Data Source Tabs (No Icons) -->
          <div class="cs-ds-tabs">
            <button
              v-for="node in projectableNodes"
              :key="node.id"
              type="button"
              class="cs-ds-tab"
              :class="{ 'cs-ds-tab--active': activeDataSource === node.id }"
              @click="activeDataSource = node.id">
              <span class="cs-ds-tab__label">{{ node.label }}</span>
              <span class="cs-ds-tab__badge">
                {{ selectedFor(node.id).length }} / {{ columnsFor(node).length }}
              </span>
            </button>

            <button
              type="button"
              class="cs-ds-tab"
              :class="{ 'cs-ds-tab--active': activeDataSource === 'all' }"
              @click="activeDataSource = 'all'">
              <span class="cs-ds-tab__label">All Tables</span>
              <span class="cs-ds-tab__badge">{{ totalSelectedCount }} / {{ totalColumnsCount }}</span>
            </button>
          </div>

          <!-- Catalog Action & Filter Bar -->
          <div class="cs-catalog-toolbar">
            <div class="cs-catalog-toolbar__left">
              <span class="cs-catalog-title">{{ activeDataSourceTitle }}</span>
              <v-chip x-small outlined class="cs-catalog-chip">
                {{ activeDataSourceKind }}
              </v-chip>

              <!-- Search Field -->
              <v-text-field
                v-model="catalogSearch"
                aria-label="Search available columns by name"
                placeholder="Search columns by name..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                hide-details
                clearable
                class="cs-hz-search" />
            </div>

            <!-- Filter Chips & Bulk Actions -->
            <div class="cs-catalog-toolbar__right">
              <div class="cs-filter-chips">
                <button
                  type="button"
                  class="cs-filter-chip"
                  :class="{ 'cs-filter-chip--active': typeFilter === 'all' }"
                  @click="typeFilter = 'all'">
                  All
                </button>
                <button
                  type="button"
                  class="cs-filter-chip"
                  :class="{ 'cs-filter-chip--active': typeFilter === 'selected' }"
                  @click="typeFilter = 'selected'">
                  Selected ({{ currentScopeSelectedCount }})
                </button>
                <button
                  type="button"
                  class="cs-filter-chip"
                  :class="{ 'cs-filter-chip--active': typeFilter === 'custom' }"
                  @click="typeFilter = 'custom'">
                  Custom (cf:*)
                </button>
                <button
                  type="button"
                  class="cs-filter-chip"
                  :class="{ 'cs-filter-chip--active': typeFilter === 'number' }"
                  @click="typeFilter = 'number'">
                  Numbers
                </button>
                <button
                  type="button"
                  class="cs-filter-chip"
                  :class="{ 'cs-filter-chip--active': typeFilter === 'date' }"
                  @click="typeFilter = 'date'">
                  Dates
                </button>
              </div>

              <div v-if="!readonly" class="cs-bulk-btns">
                <v-btn text x-small color="primary" @click="bulkToggleActive(true)">
                  Select All
                </v-btn>
                <v-btn text x-small color="secondary" @click="bulkToggleActive(false)">
                  Clear
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Multi-Column Responsive Checkbox Grid -->
          <div class="cs-catalog-grid-wrap">
            <div
              v-if="!displayedCatalogColumns.length"
              class="cs-columns__muted cs-columns__pad"
              style="text-align: center; padding: 24px 12px; width: 100%;">
              {{ catalogSearch ? 'No columns match this search filter.' : 'This node offers no columns.' }}
            </div>

            <div v-else class="cs-catalog-grid">
              <div
                v-for="item in displayedCatalogColumns"
                :key="item.node.id + '_' + item.column.name"
                role="checkbox"
                class="cs-catalog-card"
                :class="{
                  'cs-catalog-card--selected': isSelected(item.node.id, item.column.name),
                  'cs-catalog-card--disabled': readonly
                }"
                :aria-checked="String(isSelected(item.node.id, item.column.name))"
                :aria-disabled="String(readonly)"
                :aria-label="catalogColumnAriaLabel(item)"
                :tabindex="readonly ? -1 : 0"
                @click="toggleCatalogItem(item)"
                @keydown.enter.prevent="toggleCatalogItem(item)"
                @keydown.space.prevent="toggleCatalogItem(item)">
                <v-simple-checkbox
                  :value="isSelected(item.node.id, item.column.name)"
                  aria-hidden="true"
                  :tabindex="-1"
                  :ripple="false"
                  dense
                  color="primary"
                  class="cs-catalog-card__checkbox" />

                <div class="cs-catalog-card__text">
                  <span class="cs-catalog-card__name">{{ displayNameOf(item.column) }}</span>
                  <span v-if="activeDataSource === 'all'" class="cs-catalog-card__origin">
                    ({{ item.node.label }})
                  </span>
                </div>

                <span v-if="isCustomColumn(item.column)" class="cs-catalog-card__custom">
                  Custom
                </span>

                <span class="cs-type-badge" :class="typeBadgeClass(item.column)">
                  {{ typeBadgeLabel(item.column) }}
                </span>
              </div>
            </div>

            <div v-if="hiddenCatalogCount > 0" class="cs-columns__muted" style="text-align: center; padding: 6px 0;">
              {{ hiddenCatalogCount }} more columns — refine search to view.
            </div>
          </div>
        </div>

        <!-- ====================================================================
             BOTTOM PANEL: PROJECTED VIEW SCHEMA & TAG BINDING (FULL WIDTH)
             ==================================================================== -->
        <div class="cs-hz-panel cs-projected-section">
          <!-- 3D Tag Designation Hero Banner (Full Width) -->
          <div class="cs-tag-banner">
            <div class="cs-tag-banner__left">
              <div class="cs-tag-banner__badge">3D</div>
              <div>
                <div class="cs-tag-banner__title">Tag Designation (3D Model Entity Binding)</div>
                <div class="cs-tag-banner__desc">
                  Designating a tag relation supplies <code>TagId</code> (or <code>TagIds</code>) to isolate and color models in 3D views.
                </div>
              </div>
            </div>

            <v-select
              :value="model.state.tagColumnSource"
              :items="tagSourceSelectItems"
              :disabled="readonly"
              outlined
              dense
              hide-details
              class="cs-tag-banner__select"
              @change="applyTagSource" />
          </div>

          <default-spatial-focus-editor
            :model="model"
            :graph="graph"
            :readonly="readonly" />

          <!-- Scalar Auto-Projected Notice -->
          <div v-if="scalarNodes.length" class="cs-scalar-banner">
            <div class="cs-scalar-banner__left">
              <v-icon size="16" color="primary">mdi-function-variant</v-icon>
              <span><strong>Auto-Projected Relation Columns</strong>:</span>
            </div>
            <div class="cs-scalar-banner__pills">
              <div v-for="node in scalarNodes" :key="node.id" class="cs-scalar-pill">
                <span class="cs-scalar-pill__icon">fx:</span>
                <span>{{ node.label }}</span>
              </div>
            </div>
          </div>

          <!-- Canvas Header & Toolbar -->
          <div class="cs-canvas-head">
            <div class="cs-canvas-head__stats">
              <span>Projected Output Schema: <strong>{{ totalSelectedCount }} Columns</strong></span>
              <span class="cs-canvas-head__dot">&bull;</span>
              <span class="cs-columns__muted">{{ grainSummary }}</span>
            </div>

            <div v-if="!readonly" class="cs-canvas-head__actions">
              <v-btn text x-small color="primary" @click="autoPrefixAliases">
                Auto-Prefix Tables
              </v-btn>
              <v-btn text x-small color="secondary" @click="resetAliases">
                Reset Aliases
              </v-btn>
            </div>
          </div>

          <!-- Projected Columns Full-Width Table -->
          <div class="cs-projected-table-wrap">
            <div v-if="!totalSelectedCount" class="cs-projected-empty">
              <v-icon size="24" color="primary">mdi-table-column-plus-after</v-icon>
              <div class="cs-projected-empty__title">No Columns Projected Yet</div>
              <div class="cs-columns__muted">
                Tick columns from the catalog above to project them into this composite source.
              </div>
            </div>

            <table v-else class="cs-projected-table">
              <thead>
                <tr>
                  <th style="width: 170px;">Source Table</th>
                  <th style="width: 220px;">Source Column</th>
                  <th style="width: 75px;">Type</th>
                  <th>Projected Output Name (Alias in Final View)</th>
                  <th v-if="!readonly" style="width: 44px; text-align: center;">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in allProjectedItems"
                  :key="item.column.id"
                  class="cs-projected-row">
                  <td>
                    <span class="cs-node-tag">{{ item.node.label }}</span>
                  </td>
                  <td>
                    <span class="cs-projected-name">{{ selectedDisplayName(item.node, item.column) }}</span>
                  </td>
                  <td>
                    <span class="cs-type-badge" :class="typeBadgeClass(item.meta)">
                      {{ typeBadgeLabel(item.meta) }}
                    </span>
                  </td>
                  <td>
                    <v-text-field
                      :value="item.column.alias"
                      :hint="aliasNotes[item.column.id]"
                      :persistent-hint="!!aliasNotes[item.column.id]"
                      outlined
                      dense
                      hide-details="auto"
                      :readonly="readonly"
                      class="cs-alias-field"
                      @change="applyAlias(item.column, $event)" />
                  </td>
                  <td v-if="!readonly" style="text-align: center;">
                    <v-btn
                      icon
                      x-small
                      color="error"
                      class="cs-projected-remove"
                      :aria-label="`Remove ${selectedDisplayName(item.node, item.column)} from projected columns`"
                      :title="`Remove ${selectedDisplayName(item.node, item.column)} from projected columns`"
                      @click="unselect(item.node.id, item.column.column)">
                      <v-icon size="16">mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { log } from '@/utils/debugLogger';
  import {
    humanizeNodeKind,
    isTagSourceNode,
    RootNodeId,
  } from '../../model/definitionVocabulary';
  import { GraphColumnOrigin, displayNameOfColumn } from '../relations/relationsGraph';
  import DefaultSpatialFocusEditor from './DefaultSpatialFocusEditor.vue';

  /**
   * Step 3 — choose what the source projects, and which node supplies TagId.
   *
   * Horizontal (Hz) Master-Detail Layout:
   * 1. Top Section (Full Width): Data Source Tabs, Search, Type Filter Chips, and Multi-Column Checkbox Grid.
   * 2. Bottom Section (Full Width): 3D Tag Designation Hero Banner and Spacious Projected Columns Table.
   */
  const MaxRenderedColumns = 200;
  const CompilerHiddenColumns = new Set([
    '__rowkey',
    '__registerrowid',
    '__targetid',
    '__contexttagid',
  ]);

  function isHiddenCatalogColumn(column) {
    return !!column && (column.hidden === true
      || CompilerHiddenColumns.has(String(column.name || '').toLowerCase()));
  }

  export default {
    name: 'StepColumns',
    components: { DefaultSpatialFocusEditor },

    props: {
      /** definitionModel for this authoring session. */
      model: { type: Object, required: true },
      /** Authoring graph for the saved definition, or null before the first save. */
      graph: { type: Object, default: null },
      /** True while the local root graph is being resolved. */
      graphLoading: { type: Boolean, default: false },
      /** Selected workspace target: {kind, id, name}. */
      target: { type: Object, required: true },
      /** True while the source is published: selections stay visible, nothing is editable. */
      readonly: { type: Boolean, default: false },
    },

    data() {
      return {
        activeDataSource: null,
        catalogSearch: '',
        typeFilter: 'all', // 'all' | 'selected' | 'custom' | 'number' | 'date'
        aliasNotes: {},
      };
    },

    computed: {
      nodes() {
        return this.model.nodes();
      },

      /** Nodes columns can be read from: everything except the count/exists scalars. */
      projectableNodes() {
        return this.nodes.filter((node) => !node.isScalar);
      },

      scalarNodes() {
        return this.nodes.filter((node) => node.isScalar);
      },

      /**
       * Every node that reaches a tag kind, scalar or joined.
       */
      tagCapableNodes() {
        return this.nodes.filter(isTagSourceNode);
      },

      selectionsByNode() {
        return this.model.columnsByNode();
      },

      totalSelectedCount() {
        return Object.values(this.selectionsByNode).reduce((sum, list) => sum + (list ? list.length : 0), 0);
      },

      totalColumnsCount() {
        return this.projectableNodes.reduce((sum, node) => sum + this.columnsFor(node).length, 0);
      },

      activeNode() {
        if (!this.activeDataSource || this.activeDataSource === 'all') return null;
        return this.projectableNodes.find((n) => n.id === this.activeDataSource) || null;
      },

      activeDataSourceTitle() {
        if (this.activeDataSource === 'all') return 'All Available Tables';
        return this.activeNode ? this.activeNode.label : 'Select a Table';
      },

      activeDataSourceKind() {
        if (this.activeDataSource === 'all') return `${this.projectableNodes.length} Tables`;
        return this.activeNode ? this.kindLabelOf(this.activeNode) : '';
      },

      currentScopeSelectedCount() {
        if (this.activeDataSource === 'all') return this.totalSelectedCount;
        return this.activeNode ? this.selectedFor(this.activeNode.id).length : 0;
      },

      grainSummary() {
        const grainNodeId = this.model.grain();
        if (!grainNodeId || grainNodeId === RootNodeId) return 'Grain: 1 row per Root record';
        const node = this.nodes.find((candidate) => candidate.id === grainNodeId);
        return `Grain: Multiplied (fanned out by ${node ? node.label : 'Relation'})`;
      },

      tagSourceSelectItems() {
        const items = [{ text: 'No tag designation', value: null }];
        this.tagCapableNodes.forEach((node) => {
          items.push({ text: `${node.label} (${humanizeNodeKind(node.type)})`, value: node.id });
        });
        return items;
      },

      /**
       * Combines catalog columns from active data source with filtering.
       */
      filteredCatalogItems() {
        const result = [];
        const term = (this.catalogSearch || '').trim().toLowerCase();

        const targetNodes = this.activeDataSource === 'all'
          ? this.projectableNodes
          : (this.activeNode ? [this.activeNode] : []);

        for (const node of targetNodes) {
          const cols = this.columnsFor(node);
          for (const col of cols) {
            const isSel = this.isSelected(node.id, col.name);
            const isCustom = this.isCustomColumn(col);
            const typeKey = this.typeBadgeLabel(col).toLowerCase();

            // Apply type filter
            if (this.typeFilter === 'selected' && !isSel) continue;
            if (this.typeFilter === 'custom' && !isCustom) continue;
            if (this.typeFilter === 'number' && typeKey !== 'num') continue;
            if (this.typeFilter === 'date' && typeKey !== 'date') continue;

            // Apply text search
            if (term) {
              const disp = this.displayNameOf(col).toLowerCase();
              const raw = (col.name || '').toLowerCase();
              if (disp.indexOf(term) === -1 && raw.indexOf(term) === -1) continue;
            }

            result.push({ node, column: col });
          }
        }

        return result;
      },

      displayedCatalogColumns() {
        return this.filteredCatalogItems.slice(0, MaxRenderedColumns);
      },

      hiddenCatalogCount() {
        return Math.max(0, this.filteredCatalogItems.length - MaxRenderedColumns);
      },

      /**
       * Flattened list of all projected columns across all nodes for the right-hand canvas.
       */
      allProjectedItems() {
        const items = [];
        for (const node of this.projectableNodes) {
          const selected = this.selectedFor(node.id);
          const catalog = this.columnsFor(node);
          for (const col of selected) {
            const meta = catalog.find((candidate) => candidate.name === col.column) || { name: col.column };
            items.push({ node, column: col, meta });
          }
        }
        return items;
      },
    },

    watch: {
      projectableNodes: {
        immediate: true,
        handler(nodes) {
          if (!this.activeDataSource && nodes && nodes.length > 0) {
            this.activeDataSource = nodes[0].id;
          }
        },
      },
    },

    methods: {
      kindLabelOf(node) {
        return humanizeNodeKind(node.type);
      },

      columnsFor(node) {
        if (!node) return [];
        const nodes = (this.graph && this.graph.nodes) || [];
        const match = nodes.find((candidate) => candidate.kind === node.type);
        return ((match && match.columns) || []).filter(column => !isHiddenCatalogColumn(column));
      },

      selectedFor(nodeId) {
        return this.selectionsByNode[nodeId] || [];
      },

      isSelected(nodeId, columnName) {
        return this.selectedFor(nodeId).some((column) => column.column === columnName);
      },

      displayNameOf(column) {
        return displayNameOfColumn(column);
      },

      isCustomColumn(column) {
        return !!column && column.origin === GraphColumnOrigin.CustomField;
      },

      selectedDisplayName(node, column) {
        const match = this.columnsFor(node).find((candidate) => candidate.name === column.column);
        return match ? this.displayNameOf(match) : column.column;
      },

      typeBadgeLabel(column) {
        if (!column) return 'TEXT';
        const type = (column.type || column.dataType || '').toLowerCase();
        if (type.includes('int') || type.includes('num') || type.includes('float') || type.includes('dec')) return 'NUM';
        if (type.includes('date') || type.includes('time')) return 'DATE';
        if (type.includes('bool') || type.includes('bit')) return 'BOOL';
        if (column.name && (column.name.toLowerCase().endsWith('id') || column.name.toLowerCase().endsWith('guid'))) return 'ID';
        return 'TEXT';
      },

      typeBadgeClass(column) {
        const label = this.typeBadgeLabel(column);
        switch (label) {
          case 'ID': return 'cs-type-badge--id';
          case 'NUM': return 'cs-type-badge--num';
          case 'DATE': return 'cs-type-badge--date';
          case 'BOOL': return 'cs-type-badge--bool';
          default: return 'cs-type-badge--text';
        }
      },

      catalogColumnAriaLabel(item) {
        const action = this.isSelected(item.node.id, item.column.name) ? 'Deselect' : 'Select';
        return `${action} ${this.displayNameOf(item.column)} from ${item.node.label}`;
      },

      toggleCatalogItem(item) {
        const selected = this.isSelected(item.node.id, item.column.name);
        this.toggle(item.node, item.column, !selected);
      },

      toggle(node, column, checked) {
        if (this.readonly) return;
        this.model.setColumnSelected(node.id, column.name, !!checked, column.label || null);
      },

      unselect(nodeId, columnName) {
        if (this.readonly) return;
        this.model.setColumnSelected(nodeId, columnName, false);
      },

      bulkToggleActive(select) {
        if (this.readonly) return;
        const targetNodes = this.activeDataSource === 'all'
          ? this.projectableNodes
          : (this.activeNode ? [this.activeNode] : []);

        for (const node of targetNodes) {
          const cols = this.columnsFor(node);
          for (const col of cols) {
            this.model.setColumnSelected(node.id, col.name, select, col.label || null);
          }
        }
      },

      applyAlias(column, typed) {
        this.model.setColumnAlias(column.id, typed);

        const stored = this.selectedFor(column.nodeId)
          .find((candidate) => candidate.id === column.id);
        const kept = stored ? stored.alias : null;

        if (kept && typed && kept !== typed) {
          log('[StepColumns] alias normalized', { columnId: column.id, typed, kept });
          this.$set(this.aliasNotes, column.id, `Saved as "${kept}" — must be SQL-safe.`);
          return;
        }

        this.$delete(this.aliasNotes, column.id);
      },

      autoPrefixAliases() {
        if (this.readonly) return;
        for (const node of this.projectableNodes) {
          const prefix = `${node.label.replace(/[^a-zA-Z0-9]/g, '')}_`;
          const cols = this.selectedFor(node.id);
          for (const col of cols) {
            if (!col.alias.startsWith(prefix)) {
              this.model.setColumnAlias(col.id, `${prefix}${col.alias}`);
            }
          }
        }
      },

      resetAliases() {
        if (this.readonly) return;
        for (const node of this.projectableNodes) {
          const cols = this.selectedFor(node.id);
          for (const col of cols) {
            const raw = col.column.replace(/[^a-zA-Z0-9_]/g, '_');
            this.model.setColumnAlias(col.id, raw);
          }
        }
      },

      applyTagSource(nodeId) {
        this.model.setTagSource(nodeId || null);
      },

    },
  };
</script>

<style scoped>
  .cs-columns {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .cs-columns__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 64px 24px;
    text-align: center;
  }

  .cs-columns__muted {
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-columns__pad {
    padding: 6px 12px;
  }

  /* ---------------- Horizontal Stacking Layout ---------------- */
  .cs-columns-hz {
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    min-height: 0;
  }

  .cs-hz-panel {
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-sm);
    display: flex;
    flex-direction: column;
    box-shadow: var(--ds-shadow-1);
    overflow: hidden;
  }

  /* ---------------- TOP SECTION: CATALOG ---------------- */
  .cs-catalog-section {
    flex: 0 0 auto;
    max-height: 320px;
    min-height: 200px;
  }

  /* Industrial Tabs (No Icons) */
  .cs-ds-tabs {
    display: flex;
    background: var(--v-bg7-base);
    border-bottom: 1px solid var(--ds-border-default);
    overflow-x: auto;
    flex-shrink: 0;
  }

  .cs-ds-tab {
    padding: 8px 16px;
    border: none;
    border-right: 1px solid var(--ds-border-default);
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--v-text-base);
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .cs-ds-tab:hover {
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
  }

  .cs-ds-tab--active {
    background: var(--ds-surface-elevated);
    color: var(--v-text-base);
    font-weight: 600;
    border-bottom-color: var(--v-primary-base);
  }

  .cs-ds-tab__label {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cs-ds-tab__badge {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.6875rem;
    padding: 1px 6px;
    border-radius: 2px;
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
  }

  .cs-ds-tab--active .cs-ds-tab__badge {
    background: var(--ds-surface-active);
    color: var(--v-text-base);
    font-weight: 600;
  }

  /* Catalog Action & Search Bar */
  .cs-catalog-toolbar {
    padding: 8px 12px;
    background: var(--v-bg2-base);
    border-bottom: 1px solid var(--ds-border-subtle);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    flex-shrink: 0;
  }

  .cs-catalog-toolbar__left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 280px;
  }

  .cs-catalog-title {
    font-family: var(--ds-font-family-heading);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v-text-base);
    white-space: nowrap;
  }

  .cs-catalog-chip {
    font-size: 0.625rem;
    height: 18px;
    margin-right: 4px;
  }

  .cs-hz-search {
    max-width: 280px;
  }

  .cs-hz-search ::v-deep .v-input__control {
    min-height: 30px;
  }

  .cs-hz-search ::v-deep input {
    font-size: 0.75rem;
  }

  .cs-catalog-toolbar__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cs-filter-chips {
    display: flex;
    gap: 4px;
    overflow-x: auto;
  }

  .cs-filter-chip {
    padding: 2px 8px;
    border-radius: 2px;
    font-size: 0.6875rem;
    font-weight: 500;
    border: 1px solid var(--ds-border-default);
    background: var(--ds-surface-elevated);
    color: var(--v-text-base);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .cs-filter-chip:hover {
    border-color: var(--v-primary-base);
    color: var(--v-text-base);
  }

  .cs-filter-chip--active {
    background: var(--v-primary-base);
    color: #ffffff;
    border-color: var(--v-primary-base);
    font-weight: 600;
  }

  .cs-bulk-btns {
    display: flex;
    gap: 2px;
  }

  /* Multi-Column Responsive Grid */
  .cs-catalog-grid-wrap {
    flex: 1;
    overflow-y: auto;
    padding: 8px 12px;
    min-height: 0;
  }

  .cs-catalog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 6px 10px;
  }

  .cs-catalog-card {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px;
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
    border: 1px solid var(--ds-border-subtle);
    background: var(--ds-surface-elevated);
  }

  .cs-catalog-card:hover {
    background: var(--ds-surface-hover);
    border-color: var(--ds-border-strong);
  }

  .cs-catalog-card:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 2px;
  }

  .cs-catalog-card--selected {
    background: var(--ds-surface-active);
    border-color: var(--v-primary-base);
  }

  .cs-catalog-card--disabled {
    cursor: default;
    opacity: 0.72;
  }

  .cs-catalog-card__checkbox {
    margin: 0;
    padding: 0;
    pointer-events: none;
  }

  .cs-catalog-card__text {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: baseline;
    gap: 4px;
    overflow: hidden;
  }

  .cs-catalog-card__name {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--v-text-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cs-catalog-card__origin {
    font-size: 0.625rem;
    color: var(--v-text-base);
  }

  .cs-catalog-card__custom {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    padding: 1px 4px;
    border-radius: 2px;
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
    border: 1px solid var(--ds-border-default);
  }

  /* Type Badges */
  .cs-type-badge {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.625rem;
    padding: 1px 5px;
    border-radius: 2px;
    font-weight: 600;
    letter-spacing: 0.3px;
    background: var(--ds-surface-hover);
    color: var(--v-text-base);
    border: 1px solid var(--ds-border-default);
  }

  .cs-type-badge--id { border-color: var(--v-error-base); }
  .cs-type-badge--text { border-color: var(--v-blue-base); }
  .cs-type-badge--num { border-color: var(--v-golden-base); }
  .cs-type-badge--date { border-color: var(--v-purple-base); }
  .cs-type-badge--bool { border-color: var(--v-secondary-base); }

  /* ---------------- BOTTOM SECTION: PROJECTED SCHEMA & TAG BINDING ---------------- */
  .cs-projected-section {
    flex: 1;
    min-height: 240px;
    display: flex;
    flex-direction: column;
  }

  /* Full-width Tag Hero Banner */
  .cs-tag-banner {
    margin: 8px 12px 6px;
    padding: 8px 14px;
    border-radius: 4px;
    background: var(--ds-surface-active);
    border: 1px solid var(--v-secondary-base);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-shrink: 0;
  }

  .cs-tag-banner__left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .cs-tag-banner__badge {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.6875rem;
    font-weight: 700;
    padding: 3px 7px;
    border-radius: 2px;
    background: var(--v-secondary-base);
    color: var(--v-primary-darken4);
    flex-shrink: 0;
  }

  .cs-tag-banner__title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v-text-base);
  }

  .cs-tag-banner__desc {
    font-size: 0.6875rem;
    color: var(--v-text-base);
  }

  .cs-tag-banner__select {
    max-width: 260px;
    font-size: 0.75rem;
  }

  .cs-spatial-role-controls {
    margin: 0 12px 6px;
    display: grid;
    grid-template-columns: repeat(2, minmax(180px, 1fr));
    gap: 8px;
    flex-shrink: 0;
  }

  /* Scalar Banner */
  .cs-scalar-banner {
    margin: 0 12px 6px;
    padding: 6px 12px;
    border-radius: 2px;
    background: var(--ds-surface-hover);
    border: 1px solid var(--v-blue-base);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .cs-scalar-banner__left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .cs-scalar-banner__pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .cs-scalar-pill {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px;
    border-radius: 2px;
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-default);
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.6875rem;
  }

  .cs-scalar-pill__icon {
    font-weight: 600;
    color: var(--v-primary-base);
  }

  /* Canvas Head */
  .cs-canvas-head {
    padding: 6px 12px;
    background: var(--v-bg2-base);
    border-top: 1px solid var(--ds-border-default);
    border-bottom: 1px solid var(--ds-border-default);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-shrink: 0;
  }

  .cs-canvas-head__stats {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-canvas-head__dot {
    color: var(--v-text-base);
  }

  .cs-canvas-head__actions {
    display: flex;
    gap: 4px;
  }

  /* Projected Table */
  .cs-projected-table-wrap {
    flex: 1;
    overflow-y: auto;
    padding: 4px 12px 12px;
    min-height: 0;
  }

  .cs-projected-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
  }

  .cs-projected-empty__title {
    font-family: var(--ds-font-family-heading);
    font-size: 0.875rem;
    font-weight: 600;
  }

  .cs-projected-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 4px;
  }

  .cs-projected-table th {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--v-text-base);
    text-align: left;
    padding: 4px 8px;
    border-bottom: 1px solid var(--ds-border-default);
  }

  .cs-projected-row {
    background: var(--ds-surface-elevated);
    border: 1px solid var(--ds-border-subtle);
    border-radius: 2px;
    transition: all 0.15s ease;
  }

  .cs-projected-row:hover {
    background: var(--ds-surface-hover);
    border-color: var(--ds-border-strong);
  }

  .cs-projected-row td {
    padding: 5px 8px;
    font-size: 0.8125rem;
    vertical-align: middle;
  }

  .cs-node-tag {
    font-size: 0.6875rem;
    font-weight: 500;
    padding: 1px 6px;
    border-radius: 2px;
    background: var(--v-bg7-base);
    border: 1px solid var(--ds-border-default);
    white-space: nowrap;
  }

  .cs-projected-name {
    font-weight: 500;
    color: var(--v-text-base);
  }

  .cs-alias-field ::v-deep .v-input__control {
    min-height: 28px;
  }

  .cs-alias-field ::v-deep input {
    font-family: var(--ds-font-family-mono, monospace);
    font-size: 0.75rem;
    padding: 4px 6px;
  }

  .cs-projected-remove {
    opacity: 0.7;
  }

  .cs-projected-remove:hover {
    opacity: 1;
  }
</style>
